import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Dialog, IconButton, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { Close as CloseIcon } from '@material-ui/icons';

import { useConnectStore, useEnvStore } from '../../../../../../store/models';
import { getCurrency } from '../../../../../../utils';
import PricingSection from '../../../../../../components/Billing/PricingSection';
import CheckoutDrawer from '../../../../../../components/CheckoutDrawer';
import useCheckoutState from '../../../../../../components/CheckoutDrawer/useCheckoutState';
import mapPlanToCard from '../../../../../../components/Billing/mapPlanToCard';
import useDowngradePlanDialog from '../downgradePlanDialog/useDowngradePlanDialog';
import { isDowngrade, getDowngradeCopy, getPlanTier } from '../downgradePlanDialog/downgradeCopy';
import { useStyles } from './upgradePlanDialog.styles';

const TITLE_ID = 'upgrade-connect-plan-dialog-title';

function isMostPopularPlan (plan) {
    return plan.code?.includes('connect-growth');
}

const CARD_WIDTH = 300;
const CARD_GAP = 24;
const PAPER_PADDING_X = 32;
const SCROLLBAR_ALLOWANCE = 20;
const DEFAULT_MAX_WIDTH = 960; // fallback while plans load (md)

function UpgradePlanDialog ({ open, onClose, onPaymentSuccess, onScheduleConflict }) {
    const classes = useStyles();
    const theme = useTheme();
    const DIALOG_ACCENT = theme.palette.primary.main;
    const connectStore = useConnectStore();
    const envStore = useEnvStore();

    const { checkoutOpen, selectedPlan, handleChoosePlan, closeCheckout } = useCheckoutState();
    const { requestDowngradeConfirmation, renderDowngradeDialog } = useDowngradePlanDialog();

    useEffect(() => {
        connectStore.getCurrentPlan();
        connectStore.getPlanDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const plansResolved = !!connectStore.observed.availablePlans && !!connectStore.observed.currentPlan;

    const currentPlan = connectStore.currentPlan;
    const currency = (currentPlan && currentPlan.currency) || getCurrency(envStore);

    // Bridge the billing-shaped plan (flat price) into the card shape
    // PricingSection/CheckoutDrawer expect (nested price.monthly).
    const visiblePlans = useMemo(() => {
        const plans = connectStore.availablePlans || [];
        return plans.map((plan) => ({ ...mapPlanToCard(plan, currency, isMostPopularPlan(plan)), addOns: [] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectStore.observed.availablePlans, currency]);

    // Prefer an exact match on currentPlan.code against the catalog's own `code`
    // Fall back to tier matching (same convention as downgradeCopy's getPlanTier)
    // only if code is missing, and hand PricingSection the matched catalog
    // plan's own name for that fallback case.
    const currentPlanId = useMemo(() => {
        if (!currentPlan) return null;
        if (currentPlan.code) return currentPlan.code;
        const tier = getPlanTier(currentPlan.name);
        const matchedPlan = tier && visiblePlans.find((plan) => getPlanTier(plan.name) === tier);
        return (matchedPlan && matchedPlan.name) || currentPlan.name;
    }, [currentPlan, visiblePlans]);

    const dialogMaxWidth = visiblePlans.length > 0
        ? (visiblePlans.length * CARD_WIDTH) + ((visiblePlans.length - 1) * CARD_GAP) + (PAPER_PADDING_X * 2) + SCROLLBAR_ALLOWANCE
        : DEFAULT_MAX_WIDTH;

    function handleChoosePlanGated (plan, addOnValues) {
        const copy = currentPlan && isDowngrade(currentPlan, plan) ? getDowngradeCopy(currentPlan, plan) : null;
        if (copy) {
            requestDowngradeConfirmation(copy, () => handleChoosePlan(plan, addOnValues));
            return;
        }
        handleChoosePlan(plan, addOnValues);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            aria-labelledby={TITLE_ID}
            // width: '100%' (rather than MUI's own `fullWidth` prop, which applies
            // `width: calc(100% - 64px)` as a class) so `maxWidth` is a reliable
            // target size on wide screens instead of sometimes being undercut by
            // that class on viewports narrower than dialogMaxWidth + 64px.
            PaperProps={{ className: classes.paper, style: { width: '100%', maxWidth: dialogMaxWidth } }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(10,14,40,0.45)',
                    backdropFilter: 'blur(3px)',
                },
            }}
        >
            <div className={classes.header}>
                <div>
                    <Typography id={TITLE_ID} className={classes.title}>
                        Change Plan
                    </Typography>
                    {currentPlan && (
                        <Typography className={classes.subtitle}>
                            Currently on <strong>{currentPlan.name}</strong>
                            {currentPlan.price && ` · $${Number(currentPlan.price).toLocaleString()}/mo`}
                        </Typography>
                    )}
                </div>
                <IconButton
                    aria-label="Close"
                    className={classes.closeButton}
                    onClick={onClose}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>

            <div className={classes.body}>
                <PricingSection
                    plans={visiblePlans}
                    loading={!plansResolved}
                    onChoosePlan={handleChoosePlanGated}
                    currentPlanId={currentPlanId}
                    accentColor={DIALOG_ACCENT}
                    currency={currency}
                    ctaLabelPrefix="SUBSCRIBE TO"
                    cardWidth={CARD_WIDTH}
                    cardGap={CARD_GAP}
                />
            </div>

            <CheckoutDrawer
                open={checkoutOpen}
                plan={selectedPlan}
                onClose={closeCheckout}
                onSubscribed={() => connectStore.getCurrentPlan()}
                onSuccess={({ planName }) => {
                    // onSubscribed above already refreshed currentPlan on
                    // success, so we don't refetch here — doing so would hit
                    // /connect/plans a second time.
                    closeCheckout();
                    onClose();
                    if (onPaymentSuccess) {
                        onPaymentSuccess(
                            `Change to ${planName} scheduled`,
                            `Your payment details have been saved. Your plan will change to ${planName} at the end of your current billing cycle.`
                        );
                    }
                }}
                onScheduleConflict={(message) => {
                    closeCheckout();
                    onClose();
                    if (onScheduleConflict) {
                        onScheduleConflict(
                            'Plan change failed',
                            message.charAt(0).toUpperCase() + message.slice(1) + '.'
                        );
                    }
                }}
            />

            {renderDowngradeDialog()}
        </Dialog>
    );
}

UpgradePlanDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onPaymentSuccess: PropTypes.func,
    onScheduleConflict: PropTypes.func,
};

const ObservedUpgradePlanDialog = observer(UpgradePlanDialog);
export default ObservedUpgradePlanDialog;
